import React, { useEffect, useMemo, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import Link from '@mui/material/Link';
import Paper from '@mui/material/Paper';
import CircularProgress from '@mui/material/CircularProgress';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { Product } from '../data/products';
import { fetchProductById } from '../api/productsApi';

interface ProductPageProps {
  addToCart: (product: Product, quantity?: number) => void;
}

const categoryLabels: Record<string, string> = {
  'insectos-aranas': 'Insectos y Arañas',
  roedores: 'Roedores',
  jardin: 'Jardín',
  mascotas: 'Mascotas',
  'uso-humano': 'Uso Humano',
  'limpieza-desinfeccion': 'Limpieza',
  'auto-hogar': 'Auto y Hogar',
};

const ProductPage: React.FC<ProductPageProps> = ({ addToCart }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [qty, setQty] = useState(1);
  const [product, setProduct] = useState<Product | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) {
      setProduct(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    fetchProductById(id)
      .then((apiProduct) => {
        if (cancelled) return;
        setProduct(apiProduct);
      })
      .catch(() => {
        if (cancelled) return;
        setProduct(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [id]);

  const productUrl = useMemo(() => {
    if (!id) return '';
    if (typeof window === 'undefined') return `/producto/${id}`;
    return `${window.location.origin}/producto/${id}`;
  }, [id]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
        <CircularProgress color="success" />
      </Box>
    );
  }

  if (!product) {
    return (
      <Box sx={{ maxWidth: 900, mx: 'auto', px: 2, py: 8, textAlign: 'center' }}>
        <Typography variant="h5" fontWeight="bold" sx={{ mb: 2 }}>
          Producto no encontrado
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')}>
          Volver al inicio
        </Button>
      </Box>
    );
  }

  const categoryLabel = categoryLabels[product.category] || product.category;

  return (
    <Box sx={{ width: '100%', maxWidth: 1200, mx: 'auto', px: { xs: 2, md: 3 }, py: { xs: 3, md: 4 } }}>
      <Button
        startIcon={<ArrowBackIcon />}
        onClick={() => navigate(-1)}
        sx={{ mb: 2, color: '#388e3c' }}
      >
        Volver
      </Button>

      <Breadcrumbs sx={{ mb: 3 }}>
        <Link component={RouterLink} to="/" underline="hover" color="inherit">
          Inicio
        </Link>
        <Typography color="text.secondary">{categoryLabel}</Typography>
        <Typography color="text.primary" fontWeight="bold">{product.name}</Typography>
      </Breadcrumbs>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.05fr 1fr' },
          gap: { xs: 3, md: 5 },
          alignItems: 'start',
        }}
      >
        {/* Imagen izquierda */}
        <Paper
          elevation={0}
          sx={{
            bgcolor: '#f7f7f7',
            borderRadius: 3,
            border: '1px solid rgba(0,0,0,0.06)',
            p: { xs: 2, md: 3 },
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: { xs: 280, md: 480 },
          }}
        >
          <Box
            component="img"
            src={product.image}
            alt={product.name}
            sx={{
              maxWidth: '100%',
              maxHeight: { xs: 300, md: 460 },
              objectFit: 'contain',
            }}
          />
        </Paper>

        {/* Info derecha */}
        <Box>
          <Typography variant={isMobile ? 'h5' : 'h4'} fontWeight="bold" sx={{ mb: 1 }}>
            {product.name}
          </Typography>
          <Chip
            label={categoryLabel}
            size="small"
            sx={{ mb: 2, bgcolor: '#e8f5e8', color: '#2e7d32' }}
          />
          <Chip
            label={product.inStock ? 'Disponible' : 'Agotado'}
            size="small"
            sx={{
              mb: 2,
              bgcolor: product.inStock ? '#e8f5e8' : '#ffebee',
              color: product.inStock ? '#2e7d32' : '#c62828',
            }}
          />

          <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
            {product.description}
          </Typography>

          <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 1 }}>
            Características
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={1} sx={{ mb: 3 }}>
            {product.features.map((feature) => (
              <Chip key={feature} label={feature} variant="outlined" size="small" />
            ))}
          </Stack>

          <Divider sx={{ my: 2 }} />

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }} sx={{ mb: 3 }}>
            <TextField
              label="Cantidad"
              type="number"
              size="small"
              value={qty}
              onChange={(e) => setQty(Math.max(1, parseInt(e.target.value) || 1))}
              inputProps={{ min: 1 }}
              sx={{ width: 120 }}
              disabled={!product.inStock}
            />
            <Button
              variant="contained"
              size="large"
              startIcon={<ShoppingCartIcon />}
              disabled={!product.inStock}
              onClick={() => addToCart(product, qty)}
              sx={{
                bgcolor: '#4caf50',
                '&:hover': { bgcolor: '#388e3c' },
                px: 3,
              }}
            >
              Agregar al carrito
            </Button>
            <Button
              variant="outlined"
              size="large"
              startIcon={<WhatsAppIcon />}
              disabled={!product.inStock}
              onClick={() => {
                const msg = encodeURIComponent(
                  `Hola, quiero cotizar: ${product.name} x${qty}\n${productUrl}`
                );
                window.open(`https://wa.me/526622055654?text=${msg}`, '_blank');
              }}
              sx={{ borderColor: '#25d366', color: '#128c7e' }}
            >
              Cotizar WhatsApp
            </Button>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: 2.5,
              borderRadius: 2,
              border: '1px solid rgba(0,0,0,0.08)',
              bgcolor: '#fafafa',
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: 2.5,
            }}
          >
            <Box
              sx={{
                bgcolor: '#fff',
                p: 1.5,
                borderRadius: 2,
                border: '1px solid rgba(0,0,0,0.06)',
                lineHeight: 0,
              }}
            >
              <QRCodeSVG value={productUrl} size={132} level="M" includeMargin={false} />
            </Box>
            <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
              <Typography variant="subtitle1" fontWeight="bold" sx={{ mb: 0.5 }}>
                Código QR del producto
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Escanea para abrir esta página en el celular. Ideal para etiquetas y empaque.
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  wordBreak: 'break-all',
                  color: '#388e3c',
                  fontFamily: 'monospace',
                }}
              >
                {productUrl}
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default ProductPage;
